// Downloadable resources — add future guides here and they appear on /resources.
export const RESOURCES = [
  {
    slug: 'sample-content-plan',
    title: 'Sample Monthly Content Plan',
    tag: 'For social media',
    description:
      'A real example month from the Stay Visible plan: eight branded posts across four weeks, from before-and-afters to seasonal reminders. See exactly what a monthly content plan looks like before you ever sign up for one.',
    includes: ['Eight post ideas across four weeks', 'The posting rhythm we use', 'How approvals and Google profile updates work'],
    file: '/downloads/sample-content-plan.txt',
    filename: 'build-my-trade-sample-content-plan.txt',
  },
  {
    slug: 'job-photo-guide',
    title: 'How to Photograph Your Work',
    tag: 'For better photos',
    description:
      'Six phone-photo habits that take seconds and transform how your work looks online: before-and-after pairs, daylight, wide-then-detail shots, level horizons, clearing the frame and sending originals.',
    includes: ['Six practical habits', 'No equipment needed beyond your phone', 'The exact photos that sell trade work'],
    file: '/downloads/job-photo-guide.txt',
    filename: 'build-my-trade-job-photo-guide.txt',
  },
  {
    slug: 'quote-template',
    title: 'Plain-English Quote Template',
    tag: 'For winning work',
    description:
      'A ready-to-use quote layout that makes a sole trader look like a company: job description, itemised costs, deposit and payment terms, and a proper acceptance signature line. Fill in the brackets, send it as a PDF or print it.',
    includes: ['Itemised costs and totals', 'Deposit and payment terms wording', 'Acceptance signature section'],
    file: '/downloads/quote-template.txt',
    filename: 'build-my-trade-quote-template.txt',
  },
  {
    slug: 'invoice-template',
    title: 'Matching Invoice Template',
    tag: 'For getting paid',
    description:
      'The invoice that picks up where the quote leaves off: same layout, same tone, with a deposit-paid line, clear bank details and a due date — so customers know exactly what they owe and how to pay it.',
    includes: ['Matches the quote template layout', 'Deposit deduction and total due', 'Bank details and payment instructions section'],
    file: '/downloads/invoice-template.txt',
    filename: 'build-my-trade-invoice-template.txt',
  },
];


// Short companion guide shown next to the sample content plan on the FAQs page.
export const PHOTO_GUIDE = {
  title: 'How to photograph your work',
  tips: [
    { title: 'Shoot before and after', text: 'Same spot, same angle, both times. That pair of photos is the single best advert your business has.' },
    { title: 'Use daylight, not flash', text: 'Open the curtains and shoot mid-morning or afternoon. Flash flattens finish work and makes colours look wrong.' },
    { title: 'Wide first, then details', text: 'One photo of the whole room or project, then two or three close-ups of the bits that show your skill.' },
    { title: 'Keep the phone level', text: 'Turn on your camera grid and line it up. Straight horizons make photos look professional instantly.' },
    { title: 'Clear the shot', text: 'Move buckets, tools and packaging out of frame before you shoot. Thirty seconds of tidying transforms the photo.' },
    { title: 'Send the originals', text: 'Messaging apps shrink photos. Email them or send them “as files” so your gallery stays sharp.' },
  ],
};


// Sample one-month content plan shown on the FAQs page to demonstrate what the
// Stay Visible plan (8 posts/month) looks like in practice. Purely illustrative.
export const SAMPLE_CONTENT_PLAN = {
  month: 'April',
  planName: 'Stay Visible',
  postsPerMonth: 8,
  weeks: [
    {
      week: 'Week 1',
      posts: [
        { day: 'Tuesday', type: 'Before & After', idea: 'A recent job, two photos: the space before you started and the finished result.' },
        { day: 'Friday', type: 'Seasonal tip', idea: 'Spring checks worth doing after winter — gutters, pointing, outdoor taps.' },
      ],
    },
    {
      week: 'Week 2',
      posts: [
        { day: 'Tuesday', type: 'Customer review', idea: 'A genuine recent review, quoted with the customer’s permission, over a photo of the work.' },
        { day: 'Friday', type: 'Service spotlight', idea: 'One service explained in plain English — what it involves, how long it takes, what it typically costs to have done properly.' },
      ],
    },
    {
      week: 'Week 3',
      posts: [
        { day: 'Tuesday', type: 'Meet the business', idea: 'A short post about you — how long you’ve traded, the areas you cover, what you take pride in.' },
        { day: 'Friday', type: 'Seasonal reminder', idea: 'Diaries fill fast for summer projects — a nudge to book quotes early.' },
      ],
    },
    {
      week: 'Week 4',
      posts: [
        { day: 'Tuesday', type: 'Question answered', idea: 'A real question a customer asked recently, answered honestly in a couple of sentences.' },
        { day: 'Friday', type: 'Work in progress', idea: 'A mid-job photo with a line about what’s happening — shows you’re busy, local and trusted.' },
      ],
    },
  ],
  notes: [
    'Every post is designed once in your branding, then adapted and published to both Facebook and Instagram.',
    'The plan is agreed with you before the month starts — you approve everything, nothing appears without your say-so.',
    'Two Google Business Profile updates per month keep your listing active alongside the social posts.',
  ],
};


import {
  Globe,
  MapPin,
  Share2,
  PenTool,
  Image,
  Wrench,
} from 'lucide-react';

export const IMAGES = {
  plumbingHero: {
    url: 'https://images.unsplash.com/photo-1676210134188-4c05dd172f89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwbHVtYmVyJTIwcGlwZSUyMHJlcGFpcnxlbnwwfHx8fDE3OTI1MzYxMDl8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Plumber precision-fitting pipework in a UK home',
  },
  plumbingDetail: {
    url: 'https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Plumber installing clean steel press-fit pipes indoors',
  },
  electrical: {
    url: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHdpcmluZyUyMHdvcmt8ZW58MHx8fHwxNzkyNTM2MTA5fDA&ixlib=rb-4.1.0&q=85',
    alt: 'Electrician installing a modern consumer unit',
  },
  electricalPanel: {
    url: 'https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxlbGVjdHJpY2lhbiUyMHdpcmluZyUyMHdvcmt8ZW58MHx8fHwxNzkyNTM2MTA5fDA&ixlib=rb-4.1.0&q=85',
    alt: 'Circuit breaker panel being tested',
  },
  carpentry: {
    url: 'https://images.unsplash.com/photo-1659930087003-2d64e33181f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjB3b29kd29ya2luZyUyMGNyYWZ0fGVufDB8fHx8MTc5MjUzNjEwOXww&ixlib=rb-4.1.0&q=85',
    alt: 'Carpenter marking timber joints with a pencil and ruler',
  },
  workshop: {
    url: 'https://images.unsplash.com/photo-1608613304899-ea8098577e38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxjYXJwZW50ZXIlMjB3b29kd29ya2luZyUyMGNyYWZ0fGVufDB8fHx8MTc5MjUzNjEwOXww&ixlib=rb-4.1.0&q=85',
    alt: 'Joiner routing a timber moulding in a workshop',
  },
  decoratorHero: {
    url: 'https://images.unsplash.com/photo-1652829069834-2c05031199c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA8Mzl8MHwxfHNlYXJjaHwzfHxwYWludGVyJTIwcm9sbGluZyUyMHdhbGwlMjBpbnRlcmlvciUyMHBhaW50aW5nJTIwZGVjb3JhdGluZ3xlbnwwfHx8fDE3OTI3ODQzNDV8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Decorator rolling a fresh coat of paint onto an interior wall',
  },
  decoratorDetail: {
    url: 'https://images.unsplash.com/photo-1634638415860-cef1aafb60c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxpbnRlcmlvciUyMHdhbGwlMjBwYWludGluZyUyMGZpbmlzaGVkJTIwbW9kZXJuJTIwcm9vbSUyMGRlY29yYXRvcnxlbnwwfHx8fDE3OTI3ODQzNDV8MA&ixlib=rb-4.1.0&q=85',
    alt: 'Freshly decorated residential interior with a clean, professional finish',
  },
  rooferHero: {
    url: 'https://images.pexels.com/photos/37704251/pexels-photo-37704251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Roofer in a safety helmet laying tiles on a residential pitched roof',
  },
  rooferDetail: {
    url: 'https://images.pexels.com/photos/37521406/pexels-photo-37521406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Close-up of precision-installed slate roof tiles with a clean finish',
  },
  landscaperHero: {
    url: 'https://images.pexels.com/photos/16239801/pexels-photo-16239801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Landscaper laying stone paving slabs on a garden patio',
  },
  landscaperDetail: {
    url: 'https://images.unsplash.com/photo-1711098256574-7b497260cdc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxmaW5pc2hlZCUyMGxhbmRzY2FwZWQlMjBnYXJkZW4lMjBwYXRpbyUyMHBhdmluZyUyMHdvcmt8ZW58MHx8fHwxNzkyNzg0ODA3fDA&ixlib=rb-4.1.0&q=85',
    alt: 'Finished residential garden patio with precision stone paving',
  },
  plastererHero: {
    url: 'https://images.unsplash.com/photo-1768839725085-829e6ac7ac26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxwbGFzdGVyZXIlMjBza2ltbWluZyUyMHdhbGwlMjB0cm93ZWx8ZW58MHx8fHwxNzkyNzg1MzIzfDA&ixlib=rb-4.1.0&q=85',
    alt: 'Plasterer skimming an interior wall with a trowel',
  },
  plastererDetail: {
    url: 'https://images.unsplash.com/photo-1533628635777-112b2239b1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxwbGFzdGVyJTIwd2FsbCUyMHNtb290aCUyMHN1cmZhY2UlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzkyNzg1MzIzfDA&ixlib=rb-4.1.0&q=85',
    alt: 'Flawlessly smooth finished plaster wall with a professional skim coat',
  },
  handymanHero: {
    url: 'https://images.unsplash.com/photo-1623161551727-54c918bdcec1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjByZXBhaXJpbmclMjBmaXhpbmclMjBmdXJuaXR1cmUlMjBob21lJTIwbWFpbnRlbmFuY2V8ZW58MHx8fHwxNzkyNzg1NjQyfDA&ixlib=rb-4.1.0&q=85',
    alt: 'Handyman using a power drill for wall mounting and fixing work in a home',
  },
  handymanDetail: {
    url: 'https://images.unsplash.com/photo-1772338537689-056082f100a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjByZXBhaXJpbmclMjBmaXhpbmclMjBmdXJuaXR1cmUlMjBob21lJTIwbWFpbnRlbmFuY2V8ZW58MHx8fHwxNzkyNzg1NjQyfDA&ixlib=rb-4.1.0&q=85',
    alt: 'Handyman attaching bracket and hardware during home repair work',
  },
};

export const TRADES = [
  'Painters & Decorators',
  'Builders',
  'Plumbers',
  'Electricians',
  'Landscapers & Gardeners',
  'Roofers',
  'Plasterers',
  'Carpenters & Joiners',
  'Handymen',
  'Sole Traders',
];

export const TRADE_PAGES = [
  { slug: 'websites-for-plumbers', label: 'Plumbers', line: 'Emergency call-outs, bathrooms and boilers' },
  { slug: 'websites-for-electricians', label: 'Electricians', line: 'Consumer units, rewires and EV chargers' },
  { slug: 'websites-for-builders', label: 'Builders', line: 'Extensions, renovations and bigger quotes' },
  { slug: 'websites-for-painters-decorators', label: 'Painters & Decorators', line: 'Flawless finishes and transformations' },
  { slug: 'websites-for-roofers', label: 'Roofers', line: 'Re-roofs, storm repairs and visible trust' },
  { slug: 'websites-for-landscapers', label: 'Landscapers', line: 'Garden transformations, year-round' },
  { slug: 'websites-for-carpenters', label: 'Carpenters & Joiners', line: 'Bespoke joinery and fitted furniture' },
  { slug: 'websites-for-plasterers', label: 'Plasterers', line: 'Skimming, rendering and smooth walls' },
  { slug: 'websites-for-handymen', label: 'Handymen', line: 'Every odd job, clearly listed' },
];

export const PACKAGES = [
  {
    slug: 'get-found',
    name: 'Get Found',
    price: '£249',
    period: 'one-off',
    description: 'For tradespeople who need a professional starting point online.',
    features: [
      'Google Business Profile setup and optimisation (subject to Google’s eligibility and verification)',
      'Facebook business page created or improved',
      'Instagram business profile created or improved',
      'Branded profile image and cover design',
      'Business description and service list written in plain English',
      'Six branded social media post designs',
      'Contact details and enquiry links set up correctly',
      'Clear guidance on the information we need from you to launch',
    ],
    finePrint:
      'Google sets its own verification checks and timescales, so we can’t promise a verification or ranking date.',
  },
  {
    slug: 'look-professional',
    name: 'Look Professional',
    price: '£599',
    period: 'one-off',
    recommended: true,
    description: 'Everything you need to create a credible, professional online presence.',
    features: [
      'Everything in Get Found',
      'A responsive website of up to five pages',
      'Home, About, Services, Gallery and Contact pages',
      'Click-to-call so customers can ring you straight from the site',
      'WhatsApp contact button where requested',
      'Contact form for enquiries',
      'Basic on-page SEO',
      'Map integration where appropriate',
      'Links to your social profiles',
      'Six branded social media post designs',
      'Two reasonable rounds of revisions',
    ],
    finePrint:
      'The five-page limit covers the core pages listed above. Additional pages are quoted separately before any work starts.',
  },
  {
    slug: 'built-to-grow',
    name: 'Built to Grow',
    price: '£899',
    period: 'one-off',
    description: 'A complete digital launch for tradespeople ready to establish their brand.',
    features: [
      'Everything in Look Professional',
      'A tailored logo and basic brand identity',
      'A website of up to ten pages',
      'Expanded portfolio or project gallery',
      'Twelve branded launch post designs',
      'Branded quotation or invoice template',
      'Customer review request message template',
      'Basic website analytics setup (subject to consent and configuration)',
      'A handover guide so you can manage your site and profiles yourself',
    ],
    finePrint:
      'Includes the deliverables listed. Unlimited revisions, ongoing content creation and advertising are not included.',
  },
];

export const MONTHLY_PLANS = [
  {
    slug: 'keep-it-fresh',
    name: 'Keep It Fresh',
    price: '£39',
    period: 'per month',
    description: 'For keeping your website healthy, secure and up to date.',
    features: [
      'Basic website maintenance',
      'Platform and software updates where applicable',
      'Routine technical checks',
      'Minor content edits within a fair monthly allowance',
    ],
    finePrint:
      'Maintenance covers your website’s core software. Third-party services, hosting faults and premium plugin licences aren’t covered — but we’ll always tell you what’s needed and what it costs first.',
  },
  {
    slug: 'stay-visible',
    name: 'Stay Visible',
    price: '£149',
    period: 'per month',
    description: 'For staying active on social media without it eating your evenings.',
    features: [
      'Eight branded social media posts per month',
      'Each post adapted and published to both Facebook and Instagram',
      'Two Google Business Profile updates per month',
      'A monthly content plan agreed with you in advance',
    ],
    finePrint:
      'Posts are created once and adapted for each platform. Video filming, paid advertising, photography and replying to customer messages are not included unless separately agreed.',
  },
  {
    slug: 'your-digital-partner',
    name: 'Your Digital Partner',
    price: '£249',
    period: 'per month',
    description: 'For tradespeople who want their whole online presence looked after.',
    features: [
      'Twelve branded social media posts per month',
      'Four Google Business Profile updates per month',
      'Up to 30 minutes of website content edits per month',
      'A simple monthly performance summary in plain English',
    ],
    finePrint:
      'Posts are created once and adapted for both platforms. Video filming, paid advertising, photography, replying to customer messages and additional work are excluded unless separately agreed.',
  },
];

export const SERVICES = [
  {
    slug: 'website-design',
    icon: Globe,
    title: 'Website design for tradespeople',
    summary:
      'A fast, mobile-friendly website that shows your work properly and makes it easy for customers to call, message or enquire.',
    what: 'A professional website built specifically for a trade business — clear services, a gallery of your work, and contact options a customer can use in seconds from their phone.',
    why: 'Most customers check a business online before they call, even when they’ve been recommended. A proper website turns a recommendation into an enquiry instead of a missed job.',
    weDo: [
      'Design and build a responsive website of up to five or ten pages, depending on your package',
      'Click-to-call buttons, an optional WhatsApp button and a simple contact form',
      'A gallery to show off your finished work',
      'Basic on-page SEO so search engines can understand and index your pages',
      'Launch the site and connect it to your domain',
    ],
    includedIn: 'Included in Look Professional (£599) and Built to Grow (£899).',
    youProvide: [
      'Your business details, services and the areas you cover',
      'Photos of your real work — phone photos are fine',
      'Any logo or branding you already have',
    ],
    notGuaranteed: [
      'Search rankings or a position on Google — nobody can honestly guarantee these',
      'A set number of enquiries or jobs',
    ],
  },
  {
    slug: 'google-business-profile',
    icon: MapPin,
    title: 'Google Business Profile setup',
    summary:
      'A properly set up and optimised Google Business Profile so local customers can find you on Search and Maps.',
    what: 'Your Google Business Profile is the listing that appears in local search and on Google Maps — your services, opening hours, photos, reviews and contact details in one place.',
    why: 'For most tradespeople it’s the single most important place to be found online. An incomplete or missing profile sends local customers to your competitors.',
    weDo: [
      'Create or claim your profile and complete every section properly',
      'Write a clear business description and service list',
      'Add categories, service areas, photos and contact details',
      'Walk you through Google’s verification steps',
    ],
    includedIn: 'Included in every package, starting with Get Found (£249).',
    youProvide: [
      'Your trading name, address or service area, and contact details',
      'Access to your Google account, or help following the steps we send you',
      'Verification information if Google asks for it (sometimes a video or postcard)',
    ],
    notGuaranteed: [
      'Verification — Google decides this and timescales vary',
      'A position in the local map results',
    ],
  },
  {
    slug: 'social-profiles',
    icon: Share2,
    title: 'Facebook & Instagram business setup',
    summary:
      'Properly branded business pages that look established and point customers back to you.',
    what: 'Business profiles on Facebook and Instagram with your branding, services, photos and contact details — set up correctly from day one.',
    why: 'Customers look tradespeople up on Facebook before booking, especially for bigger jobs. A complete, branded page builds trust; an empty or personal one does the opposite.',
    weDo: [
      'Create or tidy up your Facebook business page and Instagram business profile',
      'Design branded profile images and cover graphics',
      'Complete your services, story and contact information',
      'Design six branded posts to launch with',
    ],
    includedIn: 'Included in every package, starting with Get Found (£249).',
    youProvide: [
      'Access to your existing accounts if you have them',
      'Photos of your work and team',
      'A rough idea of the services you want to promote',
    ],
    notGuaranteed: [
      'Follower counts, reach or engagement numbers',
      'Ongoing posting — that’s covered by the optional monthly plans',
    ],
  },
  {
    slug: 'logo-branding',
    icon: PenTool,
    title: 'Logo & branding design',
    summary:
      'A tailored logo and simple brand identity that looks right on a van, a quote and a website.',
    what: 'A clean, professional logo plus the basics of a brand — colours, fonts and how to use them — so everything you put out looks consistent.',
    why: 'Consistent branding makes a small business look established and trustworthy. It’s the difference between “a bloke with a van” and a company customers remember.',
    weDo: [
      'Design a tailored logo that works on your website, social profiles, quotes and workwear',
      'Define a simple colour and type palette',
      'Supply the files you need for print and digital use',
    ],
    includedIn: 'Included in Built to Grow (£899).',
    youProvide: [
      'Your trading name and any ideas or styles you like',
      'Feedback during the design rounds',
    ],
    notGuaranteed: [
      'Trademark availability — we recommend checking your name separately if that matters to you',
    ],
  },
  {
    slug: 'social-content',
    icon: Image,
    title: 'Social media content creation',
    summary:
      'Branded post designs and a monthly content plan, so your pages never look abandoned.',
    what: 'Professionally designed, branded posts for Facebook and Instagram — before-and-afters, job highlights, seasonal reminders and review showcases — planned a month ahead.',
    why: 'An active page reassures customers you’re busy, established and take pride in your work. It keeps you visible between recommendations.',
    weDo: [
      'Design branded post templates in your style',
      'Create eight or twelve posts per month, depending on your plan',
      'Adapt and publish each post to both Facebook and Instagram',
      'Agree a simple content plan with you each month',
    ],
    includedIn:
      'Launch posts are included in every package. Ongoing content is part of Stay Visible (£149/month) and Your Digital Partner (£249/month).',
    youProvide: [
      'Photos from your jobs as you go — quick phone shots are perfect',
      'Occasional approvals of the monthly plan',
    ],
    notGuaranteed: [
      'Video filming, photography or paid advertising — available separately if you ever want them',
      'Replying to comments and customer messages on your behalf',
    ],
  },
  {
    slug: 'website-care',
    icon: Wrench,
    title: 'Ongoing website care',
    summary:
      'Routine maintenance, updates and small edits so your website stays secure and working.',
    what: 'A monthly maintenance service that keeps your website’s software up to date, checks everything is working, and covers small content changes.',
    why: 'Websites need upkeep like vans do. Updates, checks and small edits stop little problems becoming expensive ones — and stop an out-of-date site costing you jobs.',
    weDo: [
      'Apply platform and software updates where applicable',
      'Run routine technical checks on forms, links and pages',
      'Make minor content edits within a fair monthly allowance',
      'Flag anything that needs a decision or a separate quote',
    ],
    includedIn: 'Available as the Keep It Fresh plan (£39/month).',
    youProvide: ['Nothing month to month — just tell us when something on the site needs changing'],
    notGuaranteed: [
      'Third-party services, hosting faults or premium plugin licences — we’ll always tell you what’s needed and the cost first',
      'Major redesigns or new pages, which are quoted separately',
    ],
  },
];

export const FAQS = [
  {
    category: 'Getting started',
    items: [
      {
        q: 'Do I need a website before contacting you?',
        a: 'No. Most people we work with have no website at all — that’s exactly who the service is built for. Tell us what you have (even if the answer is “nothing”) and we’ll recommend the right starting point.',
      },
      {
        q: 'Can you set everything up from scratch?',
        a: 'Yes. We can set up your Google Business Profile, Facebook and Instagram pages, branding and website from nothing. You just answer a few straightforward questions about your business and send us some photos of your work.',
      },
      {
        q: 'Do you work with all types of tradespeople?',
        a: 'Yes — painters and decorators, builders, plumbers, electricians, landscapers, roofers, plasterers, carpenters, joiners, handymen and any other sole trader or small trade business. The packages are the same whatever your trade.',
      },
      {
        q: 'How do I get started?',
        a: 'Request a free quote through the contact form, or choose a package and send us an enquiry. We’ll come back to you — usually within one working day — to talk it through and confirm exactly what’s included before you pay anything.',
      },
    ],
  },
  {
    category: 'Timescales & process',
    items: [
      {
        q: 'How long does a website take to build?',
        a: 'Once we have everything we need from you, a typical five-page website takes around two to three weeks. Timings depend on how quickly information, photos and approvals come back, and things like Google verification or domain registration can add time — we’ll always keep you updated.',
      },
      {
        q: 'Do I need to provide photographs?',
        a: 'Photos of your real work always perform best, and phone photos are completely fine. If you’re short of images for a particular section, we can use properly licensed stock photography as a stopgap and swap in your own later.',
      },
      {
        q: 'Can I update my website myself?',
        a: 'Yes — websites are built so you can make straightforward updates, and the Built to Grow package includes a handover guide showing you how. If you’d rather not touch it, the Keep It Fresh plan covers minor edits for you.',
      },
      {
        q: 'What happens if I need additional pages or changes?',
        a: 'Just ask. Anything beyond your package — extra pages, new sections, bigger changes — is quoted separately in plain English before any work starts. You’ll never see a surprise charge.',
      },
    ],
  },
  {
    category: 'Cost, ownership & hosting',
    items: [
      {
        q: 'Is hosting included?',
        a: 'Hosting and any website platform subscription are paid by you directly to the provider, so the accounts stay in your name. We recommend a suitable option, set it up for you, and tell you the exact ongoing cost before you commit — typically a modest monthly fee to the platform provider.',
      },
      {
        q: 'Do I own my website and domain?',
        a: 'Yes. Your domain is registered in your name and your website and business profiles belong to you wherever the platform allows it. If you ever move on, everything goes with you — there’s no lock-in.',
      },
      {
        q: 'Do I have to pay monthly?',
        a: 'No. The three packages are one-off payments. The monthly plans are completely optional extras for people who want ongoing maintenance, content or support — you’ll never be subscribed to anything without choosing it.',
      },
      {
        q: 'Can I cancel monthly support?',
        a: 'Yes. Monthly plans are rolling — you can cancel before your next billing date and nothing more is taken. There are no long contracts or cancellation fees.',
      },
    ],
  },
  {
    category: 'Google, social & results',
    items: [
      {
        q: 'Can you set up my Google Business Profile?',
        a: 'Yes — setup and optimisation are included in every package. Verification is done by Google itself, and we’ll guide you through whatever checks they ask for. Timescales are set by Google, so we can’t promise a verification date.',
      },
      {
        q: 'Can you guarantee I’ll appear first on Google?',
        a: 'No — and you should be wary of anyone who says they can. Search rankings depend on many factors nobody fully controls. What we do is set everything up properly, follow good practice and give your business the best honest chance of being found locally.',
      },
      {
        q: 'Do you manage social media after launch?',
        a: 'Yes, if you want us to. The Stay Visible and Your Digital Partner plans cover regular branded posts, Google Business Profile updates and a monthly content plan. If you’d rather post yourself, your package includes branded templates to get you going.',
      },
    ],
  },
];

export const PROBLEMS = [
  {
    title: 'Customers can’t find you online',
    text: 'When someone searches for your trade in your area, your competitors show up — and you don’t.',
  },
  {
    title: 'No website to send people to',
    text: 'A recommendation only goes so far when there’s nowhere professional to back it up.',
  },
  {
    title: 'Competitors look more established',
    text: 'Good tradespeople lose jobs to worse ones simply because they look more professional online.',
  },
  {
    title: 'Your best work goes unseen',
    text: 'Without a gallery or active profiles, years of quality work sit in your camera roll.',
  },
  {
    title: 'Enquiries slip through the cracks',
    text: 'If your number is hard to find, busy customers move on to the next name on the list.',
  },
];

export const TRUST_POINTS = [
  {
    title: 'Clear, upfront pricing',
    text: 'Fixed package prices published on this site. If something would cost extra, you’re told before any work starts — never after.',
  },
  {
    title: 'Personal service',
    text: 'You deal directly with the person building your online presence. No account managers, no ticket systems, no being passed around.',
  },
  {
    title: 'Plain English, always',
    text: 'Everything is explained in terms of what it does for your business. You’ll never need a marketing dictionary to talk to us.',
  },
  {
    title: 'You own everything',
    text: 'Your domain, website and business profiles are yours, wherever the platform allows. If you ever move on, it all goes with you.',
  },
  {
    title: 'No forced subscriptions',
    text: 'Packages are one-off. Monthly support is there if you want it — and only if you want it.',
  },
];
